import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';

import { environment } from './../../../environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { UserService } from '../user/user.service';
import {
  ACCESS_TOKEN_KEY,
  EMAIL_ACTIVATION_LINK_SENT,
} from 'src/app/constants/app.constants';
import { User } from 'src/app/interfaces/user';
import { FacebookRegister } from 'src/app/interfaces/facebookRegister';
import { isPlatformBrowser } from '@angular/common';

export interface AuthParams {
  emailAddress: string;
  password: string;
  authenticationType: UserAuthenticationType;
}

export enum UserAuthenticationType {
  Email = 0,
  PhoneNumber = 1,
  ChipNumber = 2,
  VetCode = 3,
}

export interface RegisterParams {
  emailAddress: string;
  password: string;
  referrerCode?: string;
  newsAndUpdatesEmail?: boolean;
  newsAndUpdatesSms?: boolean;
  newsAndUpdatesTelephone?: boolean;
  thirdPartyCommunicationEmail?: boolean;
  thirdPartyCommunicationSms?: boolean;
}

export interface ClaimAccountParams {
  password: string;
  emailAddress: string;
  emailVerificationCode: string;
}

export interface ActivateNoEmailUserParams {
  password: string;
  noEmailAddress: string;
  newEmailAddress: string;
}

export interface TokenData {
  iat: number;
  exp: number;
  userId: number;
}

export interface PassUpdateWithToken {
  password: string;
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthManagerService {
  private isBrowser: boolean;

  constructor(
    private http: HttpClient,
    private jwtHelper: JwtHelperService,
    private router: Router,
    private userService: UserService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  get isAuthenticated(): boolean {
    return !this.jwtHelper.isTokenExpired();
  }

  get token(): string | null {
    if (this.isBrowser) {
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    }
    return null;
  }

  get tokenData(): TokenData | null {
    if (this.isBrowser) {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      return this.jwtHelper.decodeToken(accessToken);
    }
    return null;
  }

  register(registerParams: RegisterParams) {
    // Adding referral on registration
    if (this.userService.options.referrer) {
      registerParams.referrerCode = this.userService.options.referrer;
    }
    return this.http
      .post<any>(`${environment.apiUrl}/api/users/register`, registerParams)
      .pipe(
        map((loginData) => {
          if (loginData.Result !== EMAIL_ACTIVATION_LINK_SENT) {
            // Save Access Token
            if (this.isBrowser) {
              localStorage.setItem(ACCESS_TOKEN_KEY, loginData.token);
            }
            // Removing referral once registered
            this.userService.options.referrer = null;
          }
          return loginData.Result;
        })
      );
  }

  loginWithFacebookUser(facebookRegister: FacebookRegister) {
    return this.http
      .post<any>(
        `${environment.apiUrl}/api/users/facebook-login`,
        facebookRegister,
        { observe: 'response' }
      )
      .pipe(
        map((responseData) => {
          responseData.body.responseStatus = responseData.status;
          if (responseData.body.token) {
            // Save Access Token
            if (this.isBrowser) {
              localStorage.setItem(ACCESS_TOKEN_KEY, responseData.body.token);
            }
            // Removing referral once registered
            this.userService.options.referrer = null;
          }
          return responseData.body;
        })
      );
  }

  claimAccount(claimAccountParams: ClaimAccountParams) {
    return this.http.post<User>(
      `${environment.apiUrl}/api/users/claim-account`,
      claimAccountParams
    );
  }

  activateNoEmailUser(activateNoEmailUserParams: ActivateNoEmailUserParams) {
    return this.http.post<User>(
      `${environment.apiUrl}/api/users/activate-no-email-account`,
      activateNoEmailUserParams
    );
  }

  login(authParams: AuthParams) {
    return this.http
      .post<any>(`${environment.apiUrl}/api/users/logon/v380`, authParams)
      .pipe(
        map((loginData) => {
          // verify user permissions
          const canAccessCustomerUI = this.verifyUserPermissions(loginData.token);
          // if can NOT access customer UI, then return false instead of user object
          if (!canAccessCustomerUI) return false;
          // Save Access Token
          if (this.isBrowser) {
            localStorage.setItem(ACCESS_TOKEN_KEY, loginData.token);
          }
          return loginData;
        })
      );
  }

  logout(): void {
    // Delete Access Token
    this.http
      .get<void>(`${environment.apiUrl}/api/users/me/logoff`)
      .subscribe();
    this.removeToken();
    this.router.navigate(['/']);
  }

  removeToken(): void {
    if (this.isBrowser) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    }
    this.userService.user = null;
  }

  forgotPassword(authParams: AuthParams): Observable<any> {
    return this.http.put<any>(
      `${environment.apiUrl}/api/users/forgot-password`,
      authParams
    );
  }

  setPasswordWithToken(
    passUpdateWithToken: PassUpdateWithToken
  ): Observable<any> {
    return this.http.put<any>(
      `${environment.apiUrl}/api/users/update-password`,
      passUpdateWithToken
    );
  }

  verifyUserPermissions(token): boolean {
    const customerUIPermission = 'Can logon to Customer UI';
    const decodedToken = this.jwtHelper.decodeToken(token);
    const permissions = decodedToken.permissions.split(',');

    return permissions.includes(customerUIPermission);
  }

}
